import {useNavigate} from 'react-router-dom'
import {useState} from 'react'
import {useGoToInventoryProductList} from '../product-list/InventoryProductList.navigator'
import {toast} from 'react-toastify'
import {createInventoryProductClient, type CreateInventoryProductClientProp} from '../InventoryFix.client'
import {Analytics} from "../../../analytics/analytics.events";
import { useAtom } from 'jotai'
import { inventoryFixOriginInfoReader, inventoryFixSelectedProductReader } from '../InventoryFix.state'

interface NewInventory {
  quantity: number
  lot: string
  expiration: string
  description: string
}

type ValidationResult = {
  disabled: boolean;
  hasErrors?: boolean;
};

function validateNewInventory({
  lot,
  quantity,
  expiration,
  description,
}: NewInventory): ValidationResult {
  const trimmedLot = lot?.trim() ?? '';
  const trimmedExpiration = expiration?.trim() ?? '';
  const trimmedDescription = description?.trim() ?? '';
  const hasQuantity = Boolean(quantity);

  const isValidDescription =
    trimmedDescription.length >= 10 &&
    trimmedDescription.length <= 140 &&
    !/^\d+$/.test(trimmedDescription) &&
    /[a-zA-Z0-9]/.test(trimmedDescription) &&
    !/^([a-zA-Z0-9])\1+$/.test(trimmedDescription);

  const onlyDescriptionRequired =
    hasQuantity && !trimmedLot && !trimmedExpiration && isValidDescription;

  const allFieldsValid =
    trimmedLot &&
    hasQuantity &&
    trimmedExpiration &&
    isValidDescription;

  if (onlyDescriptionRequired || allFieldsValid) {
    return { disabled: false };
  }

  return {
    disabled: true,
    hasErrors: !isValidDescription,
  };
}


export const useCreateProduct = () => {
  const navigate = useNavigate()
  const inventoryList = useGoToInventoryProductList()

  const [origin] = useAtom(inventoryFixOriginInfoReader)
  const [product] = useAtom(inventoryFixSelectedProductReader)
 
  const [newInventory, setNewInventory] = useState<NewInventory>({
    quantity: 0,
    lot: '',
    expiration: '',
    description: ''
  })
  const [loading, setLoading] = useState<boolean>(false)
  
  const canConfirm = validateNewInventory(newInventory)

  return {
    product,
    origin,
    newInventory,
    canConfirm,
    actions: {
      confirm: async () => {
        setLoading(true)
        try {
          const source = origin.container?.name || origin.location?.name!
          const newProductInventory: CreateInventoryProductClientProp = {
            ean: product.productEan,
            source,
            quantity: newInventory.quantity,
            description: newInventory.description,
          }
  
          if (newInventory.expiration) {
            newProductInventory.expirationDate = newInventory.expiration
          }
  
          if (newInventory.lot) {
            newProductInventory.lot = newInventory.lot
          }

          await createInventoryProductClient(newProductInventory)

          Analytics.inventoryFix.createProduct({
            location: {
              locationName: origin.location?.name as string,
              locationType: origin.location?.type as string,
              containerId: origin.container?.legacyId as string,
              containerName: origin.container?.name as string
            },
            count: newInventory.quantity,
            ean: product.productEan,
            description: newInventory.description,
          })

          toast.success('Se ha creado el SKU: ' + product.productSku)
          setTimeout(() => {
            setLoading(false)
            inventoryList.go(origin)
          }, 2000)
        } catch (e) {
          console.error('Error on create inventory', e)
          setLoading(false)
          toast.error('Error al crear nuevo inventario. ' + e?.message)
        }
      },
      goBack: () => {
        navigate(-1)
      },
      setQuantity: (quantity: number) => {
        setNewInventory(state => ({ ...state, quantity }))
      },
      setLot: (lot: string) => {
        setNewInventory(state => ({ ...state, lot }))
      },
      setExpiration: (expiration: string) => {
        setNewInventory(state => ({ ...state, expiration }))
      },
      onchangeDescription: (description: string) => {
        setNewInventory(state => ({ ...state, description }))
      }
    },
    loading,
    description: newInventory.description
  }
}
